import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  styled,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { DateRange } from "models/interfaces";
import {
  getDateInterval,
  isTruthy,
  last30DaysFromDate,
  last30DaysToDate,
  openErrorNotification,
} from "helpers/methods";
import strings from "global/constants/StringConstants";
import TrapFocus from "@mui/material/Unstable_TrapFocus";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";

interface Props {
  openDateRangePicker: boolean;
  setOpenDateRangePicker: (value: boolean) => void;
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  setPage?: Function;
  helperFunction?: Function;
  disabled?: boolean;
  ariaLabel?: string;
  // When true, the picker defaults to / clears to "Last 30 Days" instead of
  // an empty ("All time") range. Used by the dashboard/report screens.
  defaultToLast30Days?: boolean;
  hideShortcuts?: boolean;
  minDate?: string;
  disableFuture?: boolean;
  lockFromDateToToDate?: boolean;
  isClearable?: boolean;
  // When true, removes only the "All Time" shortcut without affecting
  // the displayed label or default/clear range.
  hideAllTime?: boolean;
  // Preset shortcut labels to hide for this instance (e.g. "Last 30 Days"
  // on a future-oriented picker where past presets make no sense).
  excludePresets?: string[];
  // Extra styles merged onto the trigger field, applied after the defaults so
  // a screen can re-skin the control without fighting specificity.
  sx?: SxProps<Theme>;
  // Replaces the default calendar glyph in front of the value.
  startIcon?: React.ReactNode;
  // Rendered after the value (and after the clear icon, when shown). Screens
  // that style the trigger as a dropdown chip use this for the caret.
  endIcon?: React.ReactNode;
  // Sizes the trigger input to its own value instead of the input default of
  // ~20 characters. Needed by screens that draw the trigger as a chip that
  // hugs its label; pair it with `width: auto` on the input in `sx`.
  autoSizeInput?: boolean;
  // What the trigger reads while no range is set. Defaults to "All time",
  // which suits a filter chip; a field asking for dates wants a prompt.
  emptyLabel?: string;
}


const formatDate = (value: any) =>
  value ? moment(value).format("YYYY-MM-DD") : "";

// Module-level styled day so Emotion caches the styles instead of
// re-serializing an inline `sx` object for every day cell on each render.
const StyledRangeDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isBetween" && prop !== "isStart" && prop !== "isEnd",
})(({ theme, isBetween, isStart, isEnd }: any) => ({
  borderRadius: 0,
  ...((isStart || isEnd) && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover, &:focus": { backgroundColor: theme.palette.primary.main },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover, &:focus": { backgroundColor: theme.palette.primary.main },
    },
  }),
  ...(isBetween && {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.text.primary,
    "&:hover, &:focus": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  }),
}));

const CustomDateRangePicker: FC<Props> = ({
  openDateRangePicker,
  setOpenDateRangePicker,
  dateRange,
  setDateRange,
  setPage,
  helperFunction,
  disabled,
  ariaLabel = "Select date range",
  defaultToLast30Days = false,
  hideShortcuts = false,
  minDate,
  disableFuture = true,
  lockFromDateToToDate = false,
  isClearable = true,
  hideAllTime = false,
  excludePresets,
  sx,
  startIcon,
  endIcon,
  autoSizeInput = false,
  emptyLabel = "All time",
}) => {
  const disabledDatePicker = disabled ?? false;
  const anchorEl = React.useRef(null);
  const currentDate = new Date();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const [isDateApplied, setIsDateApplied] = useState<boolean>(false);
  const [tempDateRange, setTempDateRange] = useState<any>(dateRange);
  const [rangeAnchor, setRangeAnchor] = useState<string | null>(null);

  const presetFilters = useMemo(
    () =>
      strings.DATE_RANGE_FILTERS.filter((filter) => {
        if (excludePresets?.includes(filter)) return false;
        return defaultToLast30Days || hideAllTime
          ? filter !== strings.allTime
          : true;
      }),
    [defaultToLast30Days, hideAllTime, excludePresets],
  );

  useEffect(() => {
    if (openDateRangePicker) {
      setTempDateRange(dateRange);
      setRangeAnchor(null);
    }
  }, [openDateRangePicker]);

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (anchorEl.current && (anchorEl.current as any).contains(event.target)) {
      return;
    }
    setDateRange(tempDateRange);
    setRangeAnchor(null);
    setOpenDateRangePicker(false);
  };

  const activeFilter = useMemo(() => {
    const match = presetFilters.find((filter) => {
      const presetRange = getDateInterval(filter, dateRange);
      return (
        formatDate(presetRange.fromDate) === formatDate(dateRange.fromDate) &&
        formatDate(presetRange.toDate) === formatDate(dateRange.toDate)
      );
    });
    return match ?? null;
  }, [presetFilters, dateRange.fromDate, dateRange.toDate]);

  const setDateInterval = (filter: string) => {
    const range = getDateInterval(filter, dateRange);
    setRangeAnchor(null);
    setDateRange({
      fromDate:
        range.fromDate !== null
          ? moment(range.fromDate).format("YYYY-MM-DD")
          : defaultToLast30Days
            ? last30DaysFromDate
            : null,
      toDate:
        range.toDate !== null
          ? moment(range.toDate).format("YYYY-MM-DD")
          : defaultToLast30Days
            ? last30DaysToDate
            : null,
    });
  };

  const handleCalendarChange = (newValue: Date | null) => {
    if (!newValue) return;
    const picked = moment(newValue).format("YYYY-MM-DD");

    if (rangeAnchor === null) {
      setDateRange({ fromDate: picked, toDate: picked });
      setRangeAnchor(picked);
      return;
    }

    const start = rangeAnchor <= picked ? rangeAnchor : picked;
    const end = rangeAnchor <= picked ? picked : rangeAnchor;
    setDateRange({ fromDate: start, toDate: end });
    setRangeAnchor(null);
  };

  const calendarValue = useMemo(() => {
    if (rangeAnchor) {
      return moment(rangeAnchor, "YYYY-MM-DD").startOf("day").toDate();
    }
    if (dateRange.toDate) {
      return moment(dateRange.toDate).startOf("day").toDate();
    }
    if (dateRange.fromDate) {
      return moment(dateRange.fromDate).startOf("day").toDate();
    }
    return moment().startOf("day").toDate();
  }, [rangeAnchor, dateRange.toDate, dateRange.fromDate]);

  const RangeDay = useCallback(
    (props: any) => {
      const { day } = props;
      const from = dateRange.fromDate ? moment(dateRange.fromDate) : null;
      const to = dateRange.toDate ? moment(dateRange.toDate) : null;
      const thisDate = moment(day).startOf("day");

      const isInRange =
        from &&
        to &&
        !thisDate.isBefore(from, "day") &&
        !thisDate.isAfter(to, "day");
      const isStart = from && thisDate.isSame(from, "day");
      const isEnd = to && thisDate.isSame(to, "day");
      const isMid = isInRange && !isStart && !isEnd;

      return (
        <StyledRangeDay
          {...props}
          isStart={!!isStart}
          isEnd={!!isEnd}
          isBetween={!!isMid}
          selected={!!(isStart || isEnd)}
        />
      );
    },
    [dateRange.fromDate, dateRange.toDate],
  );

  const calendarSlots = useMemo(() => ({ day: RangeDay }), [RangeDay]);

  const resolvedMinDate = useMemo(() => {
    return minDate ? moment(minDate).toDate() : undefined;
  }, [minDate]);

  const dateRangePicker = () => {
    return (
      <Stack direction={isMobile ? "column" : "row"} spacing={3}>
        {!hideShortcuts && (
          <Box
            sx={(theme) => ({
              borderRight: isMobile
                ? "none"
                : `1px solid ${theme.palette.neutral[200]}`,
              borderBottom: isMobile
                ? `1px solid ${theme.palette.neutral[200]}`
                : "none",
              pr: isMobile ? 0 : 2,
              minWidth: 160,
            })}
          >
            <List dense>
              {presetFilters.map((filter) => (
                <ListItemButton
                  key={filter}
                  selected={activeFilter === filter}
                  onClick={() => setDateInterval(filter)}
                  sx={{
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: "primary.light",
                      "& .MuiListItemText-primary": {
                        color: "text.primary",
                        fontWeight: 600,
                      },
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "primary.lightest",
                    },
                    "&:focus-visible": {
                      outline: (theme) =>
                        `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <ListItemText primary={filter} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Select date or date range and click Apply.
            </Typography>
            <DateCalendar
              value={calendarValue}
              minDate={resolvedMinDate}
              maxDate={disableFuture ? moment().toDate() : undefined}
              disableFuture={disableFuture}
              slots={calendarSlots}
              onChange={handleCalendarChange}
              sx={(theme) => ({
                "& .MuiPickersDay-root:not(.Mui-selected)": {
                  border: "none",
                },
                "& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)": {
                  border: `1px solid ${theme.palette.text.primary}`,
                },
              })}
            />
          </LocalizationProvider>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsDateApplied(false);
                setDateRange(tempDateRange);
                setRangeAnchor(null);
                setOpenDateRangePicker(false);
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (!isTruthy(dateRange.fromDate)) {
                  openErrorNotification("Please select From Date.");
                  return;
                }
                if (!isTruthy(dateRange.toDate)) {
                  openErrorNotification("Please select To Date.");
                  return;
                }
                setTempDateRange(dateRange);
                setIsDateApplied(!isDateApplied);
                setRangeAnchor(null);
                setOpenDateRangePicker(false);
                setPage && setPage(10);
                helperFunction && helperFunction(dateRange);
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Stack>
    );
  };

  const getTriggerValue = () => {
    const formatValue = (dateStr: any) => {
      if (!dateStr) return "";
      return moment(dateStr, "YYYY-MM-DD").format("MMMM D, YYYY");
    };

    const fromFormatted = formatValue(dateRange.fromDate);
    const toFormatted = formatValue(dateRange.toDate);

    if (defaultToLast30Days) {
      if (
        dateRange.fromDate === last30DaysFromDate &&
        dateRange.toDate === last30DaysToDate
      ) {
        return "Last 30 Days";
      }
      if (fromFormatted && toFormatted) {
        return `${fromFormatted} to ${toFormatted}`;
      }
      return "Last 30 Days";
    }
    return fromFormatted && toFormatted
      ? `${fromFormatted} to ${toFormatted}`
      : emptyLabel;
  };

  const showClearIcon = () => {
    if (!isClearable) return false;
    if (defaultToLast30Days) {
      return !(
        dateRange.fromDate === last30DaysFromDate &&
        dateRange.toDate === last30DaysToDate
      );
    }
    return isTruthy(dateRange.fromDate) || isTruthy(dateRange.toDate);
  };

  const handleClear = (e: any) => {
    e.stopPropagation();
    const clearedRange: DateRange = defaultToLast30Days
      ? { fromDate: last30DaysFromDate, toDate: last30DaysToDate }
      : { fromDate: null, toDate: null };
    setDateRange(clearedRange);
    setPage && setPage(10);
    helperFunction && helperFunction(clearedRange);
    setOpenDateRangePicker(false);
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Stack direction={"row"} spacing={2} alignItems={"stretch"} sx={{ width: "100%" }}>
        <TextField
          disabled={disabledDatePicker}
          ref={anchorEl}
          fullWidth
          variant="outlined"
          value={getTriggerValue()}
          onKeyDown={(e: any) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              if (openDateRangePicker) {
                setDateRange(tempDateRange);
                setRangeAnchor(null);
              }
              setOpenDateRangePicker(!openDateRangePicker);
            }
          }}
          onClick={(e: any) => {
            e.stopPropagation();
            if (openDateRangePicker) {
              setDateRange(tempDateRange);
              setRangeAnchor(null);
            }
            setOpenDateRangePicker(!openDateRangePicker);
          }}
          inputProps={{
            "aria-label": ariaLabel,
            "aria-haspopup": "dialog",
            // `size` is the cross-browser floor; `field-sizing: content` (set
            // by the caller's `sx`) refines it to the exact text width where
            // it is supported.
            ...(autoSizeInput
              ? { size: Math.max(1, getTriggerValue().length) }
              : {}),
          }}
          sx={[
            {
              minWidth: { xs: "100%", sm: 380 },
              "& .MuiOutlinedInput-input": {
                cursor: "pointer",
                fontSize: "13px",
                paddingLeft: "0px !important",
                paddingRight: "0px !important",
                color: "text.primary",
                fontWeight: 600,
              },
              "& .MuiInputBase-input": {
                cursor: "pointer",
                fontSize: "13px",
                paddingLeft: "0px !important",
                paddingRight: "0px !important",
                color: "text.primary",
                fontWeight: 600,
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: (theme: Theme) =>
                  theme.palette.text.disabled,
                color: "text.disabled",
              },
              "&.Mui-disabled .MuiSvgIcon-root": {
                color: "text.disabled",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: 0,
              },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "common.white",
                },
                "&:hover fieldset": {
                  borderColor: "common.white",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "common.white",
                },
              },
            },
            ...(Array.isArray(sx) ? sx : [sx ?? {}]),
          ]}
          InputProps={{
            readOnly: true,
            startAdornment: startIcon ?? (
              <DateRangeIcon
                fontSize="small"
                sx={{
                  cursor: "pointer",
                  mr: 1,
                  width: "1.5rem",
                  color: disabledDatePicker
                      ? "text.disabled"
                      : "text.primary",
                }}
                role="presentation"
              />
            ),
            endAdornment: (
              <>
                {showClearIcon() && (
                  <Tooltip title={"Clear"} placement="right" arrow>
                    <ClearIcon
                      role="button"
                      tabIndex={0}
                      aria-label="Clear date range"
                      fontSize="small"
                      htmlColor="grey"
                      sx={{ cursor: "pointer" }}
                      onClick={handleClear}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          handleClear(e);
                        }
                      }}
                    />
                  </Tooltip>
                )}
                {endIcon}
              </>
            ),
          }}
        />
      </Stack>
      <Popper
        sx={{
          zIndex: 1300,
          boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
          borderRadius: "10px",
          background: "common.white",
        }}
        open={openDateRangePicker}
        anchorEl={anchorEl.current}
        placement={"bottom-start"}
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClickAway}>
            <div>
              <TrapFocus open={openDateRangePicker}>
                <Fade {...TransitionProps}>
                  <Paper
                    role="dialog"
                    aria-modal="true"
                    aria-label="Select date range"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setDateRange(tempDateRange);
                        setRangeAnchor(null);
                        setOpenDateRangePicker(false);
                      }
                    }}
                    sx={{
                      borderRadius: "10px",
                      overflow: "hidden",
                      padding: "16px",
                      backgroundColor: "common.white",
                    }}
                  >
                    {dateRangePicker()}
                  </Paper>
                </Fade>
              </TrapFocus>
            </div>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
};

export default CustomDateRangePicker;
